import type { Country } from "../data";

export type ScenarioInputs = {
  budget: number;
  communities: number;
  languagePacks: number;
  validationIntensity: number;
  adoptionProbability: number;
  serviceValue: number;
};

export type ScenarioOutputs = {
  expectedReach: number;
  successfulNavigations: number;
  costPerSuccess: number;
  benefitCostRatio: number;
  netBenefit: number;
  readiness: number;
  evidencePower: number;
  sensitivity: { label: string; value: number }[];
};

export const defaultScenario: ScenarioInputs = {
  budget: 200000,
  communities: 208,
  languagePacks: 16,
  validationIntensity: 72,
  adoptionProbability: 62,
  serviceValue: 54,
};

export function scenarioFromCountry(country: Country): ScenarioInputs {
  return {
    budget: country.role === "Pilot country" ? 100000 : country.countryAllocation,
    communities: country.communities,
    languagePacks: country.languages.length + country.readinessBriefs.length,
    validationIntensity: Math.round((country.validators / Math.max(country.communities, 1)) * 48),
    adoptionProbability: country.adoptionPct,
    serviceValue: country.role === "Pilot country" ? 56 : 48,
  };
}

export function modelScenario(input: ScenarioInputs): ScenarioOutputs {
  const adoption = input.adoptionProbability / 100;
  const validation = Math.min(1, input.validationIntensity / 100);
  const languageDepth = Math.min(1.15, input.languagePacks / 14);
  const evidenceReliability = 0.62 + validation * 0.25 + Math.min(input.languagePacks, 20) * 0.0065;
  const expectedReach = Math.round(input.communities * (9 + input.languagePacks * 1.35) * (0.75 + adoption));
  const successfulNavigations = Math.round(expectedReach * adoption * evidenceReliability * languageDepth);
  const benefits = successfulNavigations * input.serviceValue * (1.14 + validation * 0.28);
  const costPerSuccess = input.budget / Math.max(successfulNavigations, 1);
  const benefitCostRatio = benefits / input.budget;
  const netBenefit = benefits - input.budget;
  const readiness = Math.min(
    100,
    Math.round(24 + adoption * 29 + validation * 23 + Math.min(input.languagePacks, 18) * 1.15 + Math.min(input.communities, 240) * 0.045),
  );
  const evidencePower = Math.min(100, Math.round(30 + validation * 38 + Math.log10(input.communities + 10) * 12));
  return {
    expectedReach,
    successfulNavigations,
    costPerSuccess,
    benefitCostRatio,
    netBenefit,
    readiness,
    evidencePower,
    sensitivity: [
      { label: "Adoption", value: Math.round(adoption * 100) },
      { label: "Validation", value: Math.round(validation * 100) },
      { label: "Language depth", value: Math.round(languageDepth * 82) },
      { label: "Evidence power", value: evidencePower },
      { label: "Readiness", value: readiness },
    ],
  };
}

export function formatCurrency(value: number, compact = false): string {
  if (compact) {
    return new Intl.NumberFormat("en", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat("en", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en").format(value);
}
